import React, { useEffect, useState } from "react";
import {
    Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, IconButton, Popover, Tooltip, Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import QuizIcon from "@mui/icons-material/Quiz";
import GradingIcon from "@mui/icons-material/Grading";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useCurrUserUtility } from "../contexts/useCurrUserUtility";
import { useNavigate } from "react-router-dom";
import { DateUtility } from "../helper/DateUtility";
import { NotificationItemModel } from "../generated/api";
import { NotificationService } from "../service/NotificationService";
import { sseConnectionManager } from "../service/SseConnectionManager";
import Notifier from "../helper/notifier";

// ── Notification type → icon + colour ────────────────────────────────────────
const typeConfig: Record<number, { icon: React.ReactNode; color: string }> = {
    1: { icon: <SchoolIcon fontSize="small" />,        color: "#309255" }, // NewCourse
    2: { icon: <EditNoteIcon fontSize="small" />,      color: "#1565c0" }, // CourseUpdated
    3: { icon: <LibraryBooksIcon fontSize="small" />,  color: "#309255" }, // NewLecture
    4: { icon: <MenuBookIcon fontSize="small" />,      color: "#1565c0" }, // LectureUpdated
    5: { icon: <MenuBookIcon fontSize="small" />,      color: "#7b1fa2" }, // LectureUpdatedEnrolled
    6: { icon: <FamilyRestroomIcon fontSize="small" />,color: "#e65100" }, // ParentInvitation
    7:  { icon: <HowToRegIcon fontSize="small" />,      color: "#00796b" }, // LectureInvitation
    8:  { icon: <PersonAddAlt1Icon fontSize="small" />, color: "#c62828" }, // LectureJoinRequest
    9:  { icon: <ThumbUpAltIcon fontSize="small" />,    color: "#2e7d32" }, // InvitationAccepted
    10: { icon: <ThumbDownAltIcon fontSize="small" />,  color: "#b71c1c" }, // InvitationRejected
    11: { icon: <ChildCareIcon fontSize="small" />,     color: "#2e7d32" }, // ParentLinkAccepted
    12: { icon: <PersonOffIcon fontSize="small" />,     color: "#b71c1c" }, // ParentLinkRejected
    13: { icon: <PlayCircleIcon fontSize="small" />,     color: "#309255" }, // LectureStarting
    18: { icon: <QuizIcon fontSize="small" />,           color: "#309255" }, // ExamAssigned
    19: { icon: <GradingIcon fontSize="small" />,        color: "#1565c0" }, // ExamGraded
    20: { icon: <WorkspacePremiumIcon fontSize="small" />, color: "#c9a227" }, // CertificateIssued
};

const getTypeConfig = (typeStr?: string) => {
    const n = parseInt(typeStr ?? "0", 10);
    return typeConfig[n] ?? { icon: <NotificationsNoneIcon fontSize="small" />, color: "#757575" };
};

// Matches NotificationTypeEnum.LectureStarting on the backend (Common/Models/Enums.cs).
// This one is live-push-only (see SSEService/DbPollingService) — it never lands in
// the Notifications table, so it only ever arrives here as a fresh SSE message.
const LECTURE_STARTING_TYPE = 13;

// ─────────────────────────────────────────────────────────────────────────────

export const NotificationMenu = () => {
    const [notifications, setNotifications] = useState<NotificationItemModel[]>([]);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [lectureAlert, setLectureAlert] = useState<NotificationItemModel | null>(null);
    const { isAuth, getCurrUserToken } = useCurrUserUtility();
    const navigate = useNavigate();

    const unreadCount = notifications.filter(n => !n.isSeen).length;

    // ── Load + SSE stream ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!isAuth()) return;
        const svc = new NotificationService(navigate);
        const userToken = getCurrUserToken?.() ?? "";

        // Load all existing notifications on mount
        svc.GetNotifications(1, 100).then(list => setNotifications(list ?? []));

        // Registers this logged-in user's single shared SSE connection (idempotent —
        // other consumers, e.g. the Messages page, subscribe without reconnecting).
        sseConnectionManager.connect(userToken);

        const off = sseConnectionManager.on("notification", (data: NotificationItemModel) => { 
            setNotifications(prev => {
                if (prev.some(n => n.token === data.token)) return prev;
                return [data, ...prev].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                );
            });

            // Lecture-starting reminders also interrupt with a popup — waiting for
            // someone to open the bell dropdown isn't good enough for "join now".
            if (parseInt(data.notificationType ?? "0", 10) === LECTURE_STARTING_TYPE) {
                setLectureAlert(data);
            }
            else
                Notifier.showSuccess(data.label);
        });

        return () => {
            off();
            sseConnectionManager.disconnect();
        };
    }, [navigate]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
    const handleClose = () => setAnchor(null);

    const handleClick = (item: NotificationItemModel) => {
        if (!item.isSeen) {
            const svc = new NotificationService(navigate);
            svc.SetSeen(item.token);
            setNotifications(prev =>
                prev.map(n => n.token === item.token ? { ...n, isSeen: true } : n)
            );
        }
        handleClose();
        navigate(item.url);
    };

    const markAllSeen = () => {
        const svc = new NotificationService(navigate);
        const userToken = getCurrUserToken?.();
        if (userToken) svc.MarkAllSeen?.(userToken);
        setNotifications(prev => prev.map(n => ({ ...n, isSeen: true })));
    };

    const handleJoinLecture = () => {
        if (lectureAlert) navigate(lectureAlert.url);
        setLectureAlert(null);
    };

    return (
        <>
            <Tooltip title={unreadCount > 0 ? `${unreadCount} unread` : "Notifications"}>
                <IconButton size="small" onClick={handleOpen} sx={{ color: "inherit" }}>
                    <Badge badgeContent={unreadCount} color="error"
                        sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: 10 } }}>
                        {unreadCount > 0
                            ? <NotificationsActiveIcon sx={{ fontSize: 22 }} />
                            : <NotificationsNoneIcon  sx={{ fontSize: 22 }} />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top",    horizontal: "right" }}
                slotProps={{
                    paper: {
                        elevation: 8,
                        sx: { width: 380, borderRadius: 3, mt: 1, overflow: "hidden" },
                    },
                }}
            >
                {/* Header */}
                <Box sx={{
                    background: "linear-gradient(135deg,#309255 0%,#1e6040 100%)",
                    px: 2, py: 1.5,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <NotificationsActiveIcon sx={{ color: "#fff", fontSize: 18 }} />
                        <Typography fontWeight={800} fontSize={15} color="#fff">
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip label={unreadCount} size="small"
                                sx={{ bgcolor: "#e53935", color: "#fff", fontWeight: 700, fontSize: 11, height: 20, minWidth: 20 }} />
                        )}
                    </Box>
                    {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                            <IconButton size="small" onClick={markAllSeen} sx={{ color: "rgba(255,255,255,0.85)", p: 0.5 }}>
                                <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* List */}
                {notifications.length === 0 ? (
                    <Box py={5} textAlign="center">
                        <NotificationsNoneIcon sx={{ fontSize: 48, color: "#ddd", mb: 1 }} />
                        <Typography fontSize={14} color="text.secondary">No notifications yet</Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                        {notifications.map((item, idx) => {
                            const cfg = getTypeConfig(item.notificationType);
                            return (
                                <React.Fragment key={item.token}>
                                    <Box
                                        onClick={() => handleClick(item)}
                                        sx={{
                                            display: "flex", alignItems: "flex-start", gap: 1.5,
                                            px: 2, py: 1.5, cursor: "pointer",
                                            bgcolor: item.isSeen ? "transparent" : "rgba(48,146,85,0.06)",
                                            borderLeft: item.isSeen ? "3px solid transparent" : "3px solid #309255",
                                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                                            transition: "background 0.15s",
                                        }}
                                    >
                                        {/* Type icon */}
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                                            bgcolor: `${cfg.color}18`,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: cfg.color,
                                        }}>
                                            {cfg.icon}
                                        </Box>

                                        {/* Content */}
                                        <Box flex={1} minWidth={0}>
                                            <Typography
                                                fontSize={13}
                                                fontWeight={item.isSeen ? 400 : 600}
                                                color={item.isSeen ? "text.secondary" : "text.primary"}
                                                sx={{ lineHeight: 1.4 }}
                                                dangerouslySetInnerHTML={{ __html: item.label }}
                                            />
                                            <Typography fontSize={11} color="text.disabled" mt={0.25}>
                                                {DateUtility.DurationText(item.date, undefined)}
                                            </Typography>
                                        </Box>

                                        {/* Unread dot */}
                                        {!item.isSeen && (
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: "50%",
                                                bgcolor: "#309255", flexShrink: 0, mt: 0.75,
                                            }} />
                                        )}
                                    </Box>
                                    {idx < notifications.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                )}
            </Popover>

            {/* Lecture-starting popup — pulled out of the bell dropdown so it grabs
                attention regardless of what page the user is on. */}
            <Dialog open={!!lectureAlert} onClose={() => setLectureAlert(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlayCircleIcon sx={{ color: "#309255" }} />
                    Your Lecture is Starting!
                </DialogTitle>
                <DialogContent>
                    <Typography
                        fontSize={14}
                        dangerouslySetInnerHTML={{ __html: lectureAlert?.label ?? "" }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setLectureAlert(null)}>Dismiss</Button>
                    <Button variant="contained" onClick={handleJoinLecture}
                        sx={{ bgcolor: "#309255", "&:hover": { bgcolor: "#237a44" } }}>
                        Join Now
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
